import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { utcToLocal } from 'utils/convertLocalFormat';
import {
  Box,
  Text,
  Group,
  Badge,
  Paper,
  Grid,
  ActionIcon,
  Modal,
  Stack,
  Title,
  SegmentedControl,
  Tooltip,
  Button
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCalendarEvent,
  IconClock,
  IconX,
  IconInfoCircle,
  IconArrowRight,
  IconHistory,
  IconChevronDown,
  IconList
} from '@tabler/icons-react';

interface ModuleBreakdown {
  moduleType: string;
  moduleStart: string;
  moduleEnd: string;
}

interface HistoryTransaction {
  startDate: string;
  endDate: string;
}

interface ScheduleCourse {
  msLCMSCourseID: string;
  courseName: string;
  courseCode: string;
  learningHours: number;
  deliveryType?: string;
  modeOfDelivery?: string;
  planningStart: string | null;
  planningEnd: string | null;
  trLCMSPlanningCoursesID: string;
  listModule?: ModuleBreakdown[];
  isCEAReady?: boolean;
  budget?: number;
  statusCourse?: string;
  historyTransaksi?: HistoryTransaction[];
}

// Helper function to ensure date string has UTC timezone suffix
const ensureUTCFormat = (dateString: string): string => {
  if (!dateString) return dateString;
  // If the date string doesn't end with 'Z' and looks like ISO format, add 'Z'
  if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/) && !dateString.endsWith('Z')) {
    return dateString + 'Z';
  }
  return dateString;
};

interface ReadOnlyCalendarScheduleViewProps {
  courses: ScheduleCourse[];
  initialDate?: Date;
  filterStartDate?: Date | null;
  filterEndDate?: Date | null;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  course: ScheduleCourse;
  color: string;
}

interface RibbonEvent {
  event: CalendarEvent;
  startCol: number;
  endCol: number;
  row: number;
  layer: number;
  isMultiWeek?: boolean;
  weekStartCol?: number;
  weekEndCol?: number;
}

type CalendarViewType = 'month' | 'week' | 'day';

// Generate consistent colors for courses
const getCourseColor = (courseId: string): string => {
  const colors = [
    '#228be6',
    '#fa5252',
    '#40c057',
    '#fd7e14',
    '#845ef7',
    '#15aabf',
    '#e599f7',
    '#51cf66',
    '#ff8cc8',
    '#74c0fc',
    '#ffd43b',
    '#69db7c',
    '#ff6b6b',
    '#4c6ef5',
    '#9775fa',
    '#22b8cf',
    '#f76707',
    '#495057',
    '#868e96',
    '#adb5bd'
  ];

  let hash = 0;
  for (let i = 0; i < courseId.length; i++) {
    hash = courseId.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

const ReadOnlyCalendarScheduleView: React.FC<ReadOnlyCalendarScheduleViewProps> = ({
  courses,
  initialDate,
  filterStartDate,
  filterEndDate
}) => {
  const [calendarView, setCalendarView] = useState<CalendarViewType>('month');
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventModalOpened, setEventModalOpened] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Sync currentDate when initialDate prop changes
  useEffect(() => {
    if (initialDate) {
      setCurrentDate(initialDate);
    }
  }, [initialDate]);

  // Convert courses to calendar events
  const events = useMemo(() => {
    // API returns UTC datetimes without 'Z' (e.g. "2026-06-30T19:00:00" = July 1 02:00 WIB).
    // utcToLocal interprets the string as UTC and converts to local time.
    // Then strip the time component so calendar placement is date-only (midnight local).
    const parseDateLocal = (dateStr: string): Date => {
      const local = utcToLocal(dateStr);
      return new Date(local.getFullYear(), local.getMonth(), local.getDate());
    };

    const toLocalDateStr = (dateStr: string): string => {
      if (!dateStr) return '';
      const d = parseDateLocal(dateStr);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const seenCourseDateKeys = new Set<string>();
    const seenCourseNameDateKeys = new Set<string>();
    const uniqueCourses = courses.filter((course) => {
      const startDate = toLocalDateStr(course.planningStart || '');
      const endDate = toLocalDateStr(course.planningEnd || '');
      const courseId = course.trLCMSPlanningCoursesID || course.msLCMSCourseID;
      const courseDateKey = `${courseId}-${startDate}-${endDate}`;
      const courseNameDateKey = `${course.courseName}-${startDate}-${endDate}`;

      if (seenCourseDateKeys.has(courseDateKey) || seenCourseNameDateKeys.has(courseNameDateKey)) {
        return false;
      }

      seenCourseDateKeys.add(courseDateKey);
      seenCourseNameDateKeys.add(courseNameDateKey);
      return true;
    });

    const eventList = uniqueCourses
      .filter((course) => course.planningStart && course.planningEnd)
      .map((course): CalendarEvent => {
        const color = getCourseColor(course.msLCMSCourseID);
        const startDate = toLocalDateStr(course.planningStart || '');
        const endDate = toLocalDateStr(course.planningEnd || '');

        return {
          id: `${course.trLCMSPlanningCoursesID || course.msLCMSCourseID}-${startDate}-${endDate}`,
          title: course.courseName,
          start: parseDateLocal(course.planningStart!),
          end: parseDateLocal(course.planningEnd!),
          course: course,
          color: color
        };
      });

    // Deduplicate events by ID to prevent duplicate ribbons
    const uniqueEvents = eventList.filter(
      (event, index, self) => self.findIndex((e) => e.id === event.id) === index
    );

    return uniqueEvents;
  }, [courses]);

  // Get days for current view
  const getCalendarDays = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (calendarView === 'month') {
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      const days = [];
      const current = new Date(startDate);

      for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      return days;
    } else if (calendarView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

      const days = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(startOfWeek);
        day.setDate(startOfWeek.getDate() + i);
        days.push(day);
      }

      return days;
    } else {
      return [new Date(currentDate)];
    }
  }, [currentDate, calendarView]);

  const calendarDays = getCalendarDays();

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(day);
    dayEnd.setHours(23, 59, 59, 999);

    if (filterStartDate) {
      const filterStart = new Date(filterStartDate);
      filterStart.setHours(0, 0, 0, 0);
      if (dayEnd < filterStart) return [];
    }

    if (filterEndDate) {
      const filterEnd = new Date(filterEndDate);
      filterEnd.setHours(23, 59, 59, 999);
      if (dayStart > filterEnd) return [];
    }

    return events.filter((event) => event.start <= dayEnd && event.end >= dayStart);
  };

  // Calculate ribbon positions for multi-day events
  const calculateRibbonEvents = useMemo((): RibbonEvent[] => {
    if (calendarView !== 'month') return [];

    const ribbons: RibbonEvent[] = [];
    const ribbonKeys = new Set<string>();

    const toStartOfDay = (value: Date) => {
      const d = new Date(value);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    // Group calendar days into weeks
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];

    calendarDays.forEach((day, index) => {
      currentWeek.push(day);
      if ((index + 1) % 7 === 0 || index === calendarDays.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });

    // Process each event across all weeks it spans
    events.forEach((event) => {
      const rawStart = toStartOfDay(event.start);
      const rawEnd = toStartOfDay(event.end);

      const eventStart =
        filterStartDate && rawStart < toStartOfDay(filterStartDate)
          ? toStartOfDay(filterStartDate)
          : rawStart;
      const eventEnd =
        filterEndDate && rawEnd > toStartOfDay(filterEndDate)
          ? toStartOfDay(filterEndDate)
          : rawEnd;

      // Check which weeks this event spans
      weeks.forEach((week, weekIndex) => {
        const weekStart = new Date(week[0]);
        const weekEnd = new Date(week[6]);
        weekStart.setHours(0, 0, 0, 0);
        weekEnd.setHours(23, 59, 59, 999);

        // Only process if event overlaps with this week
        if (event.start <= weekEnd && event.end >= weekStart) {
          // Find start and end columns within this week
          let startCol = -1;
          let endCol = -1;

          // Determine the actual start and end dates for this week
          const actualStart = eventStart.getTime() < weekStart.getTime() ? weekStart : eventStart;
          const actualEnd = eventEnd.getTime() > weekEnd.getTime() ? weekEnd : eventEnd;

          week.forEach((day, dayIndex) => {
            const dayStart = new Date(day);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);

            // Check if this day is within the event range
            if (actualStart <= dayEnd && actualEnd >= dayStart) {
              if (startCol === -1) {
                startCol = dayIndex;
              }
              endCol = dayIndex;
            }
          });

          // Fallback adjustments
          if (startCol === -1 && eventStart.getTime() < weekStart.getTime()) {
            startCol = 0;
          }
          if (endCol === -1 && eventEnd.getTime() > weekEnd.getTime()) {
            endCol = 6;
          }

          if (startCol >= 0 && endCol >= 0 && startCol <= endCol) {
            const ribbonKey = `${event.id}-${weekIndex}-${startCol}-${endCol}`;
            if (ribbonKeys.has(ribbonKey)) return;

            ribbons.push({
              event,
              startCol,
              endCol,
              row: weekIndex,
              layer: 0,
              isMultiWeek:
                eventStart.getTime() < weekStart.getTime() ||
                eventEnd.getTime() > weekEnd.getTime(),
              weekStartCol: startCol,
              weekEndCol: endCol
            });
            ribbonKeys.add(ribbonKey);
          }
        }
      });
    });

    const eventLayerMap = new Map<string, number>();

    weeks.forEach((week, weekIndex) => {
      const weekRibbons = ribbons.filter((r) => r.row === weekIndex);
      // Sort by start column then by length so longer ribbons place first
      weekRibbons.sort((a, b) => {
        if (a.startCol === b.startCol) return b.endCol - a.endCol;
        return a.startCol - b.startCol;
      });

      const layers: { start: number; end: number; eventId: string }[][] = [];

      weekRibbons.forEach((ribbon) => {
        let targetLayer = eventLayerMap.get(ribbon.event.id) ?? 0;

        // Find first layer without overlap on the same columns
        while (true) {
          if (!layers[targetLayer]) layers[targetLayer] = [];

          const overlaps = layers[targetLayer].some(
            (placed) => !(ribbon.endCol < placed.start || ribbon.startCol > placed.end)
          );

          if (!overlaps) {
            layers[targetLayer].push({
              start: ribbon.startCol,
              end: ribbon.endCol,
              eventId: ribbon.event.id
            });
            ribbon.layer = targetLayer;
            eventLayerMap.set(ribbon.event.id, targetLayer);
            break;
          }

          targetLayer += 1;
        }
      });
    });

    // Final deduplication: remove any duplicate ribbons based on unique combination
    const uniqueRibbons = ribbons.filter(
      (ribbon, index, self) =>
        self.findIndex(
          (r) =>
            r.event.id === ribbon.event.id &&
            r.row === ribbon.row &&
            r.startCol === ribbon.startCol &&
            r.endCol === ribbon.endCol
        ) === index
    );

    return uniqueRibbons;
  }, [events, calendarDays, calendarView, filterStartDate, filterEndDate]);

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  // Note: event modal is opened on hover (mouseEnter) instead of click

  const formatDateHeader = () => {
    if (calendarView === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (calendarView === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  const renderEventItem = (event: CalendarEvent, day?: Date, isCompact: boolean = false) => {
    // Only render single-day events as individual items since multi-day events use ribbons
    return (
      <Box
        key={`${event.id}-${day?.getTime() || 'single'}`}
        p={isCompact ? 6 : 8}
        mb={4}
        style={{
          background: `linear-gradient(135deg, ${event.color}, ${event.color}dd)`,
          color: 'white',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: isCompact ? 11 : 13,
          position: 'relative',
          minHeight: isCompact ? 24 : 32,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }
        }}
        onClick={() => {
          setSelectedEvent(event);
          setEventModalOpened(true);
        }}
      >
        <Group gap={4} wrap='nowrap'>
          <Text
            size={isCompact ? 'xs' : 'sm'}
            fw={600}
            lineClamp={1}
            style={{
              color: 'white',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            {event.title}
          </Text>
        </Group>

        {!isCompact && (
          <Group gap={4} mt={2}>
            <Text size='xs' style={{ color: 'rgba(255,255,255,0.9)' }}>
              {event.course.courseCode}
            </Text>
            <Text size='xs' style={{ color: 'rgba(255,255,255,0.7)' }}>
              •
            </Text>
            <Text size='xs' style={{ color: 'rgba(255,255,255,0.9)' }}>
              {event.course.learningHours}h
            </Text>
          </Group>
        )}
      </Box>
    );
  };

  const renderMonthView = () => {
    const ribbonEvents = calculateRibbonEvents;
    const weeksCount = Math.ceil(calendarDays.length / 7);
    const maxLayersPerWeek = new Array(weeksCount).fill(0);

    // Calculate required height for each week based on ribbon layers
    ribbonEvents.forEach((ribbon) => {
      maxLayersPerWeek[ribbon.row] = Math.max(maxLayersPerWeek[ribbon.row], ribbon.layer + 1);
    });

    // Height constants
    const baseCellHeight = 120; // base cell height for a week row
    const ribbonLayerHeight = 22; // height per ribbon layer

    // Calculate dynamic cell heights for each week
    const weekCellHeights = maxLayersPerWeek.map(
      (layers) => baseCellHeight + layers * ribbonLayerHeight
    );

    return (
      <Box>
        {/* Days of week header */}
        <Grid gutter={0} mb='sm'>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid.Col key={day} span={12 / 7}>
              <Text ta='center' fw={500} size='sm' c='dimmed'>
                {day}
              </Text>
            </Grid.Col>
          ))}
        </Grid>

        {/* Calendar grid with ribbons */}
        <Box style={{ position: 'relative' }}>
          <Grid gutter={1}>
            {calendarDays.map((day, index) => {
              const dayEvents = getEventsForDay(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isToday = day.toDateString() === new Date().toDateString();

              // All events (including single-day) are rendered as ribbons; keep cell list empty to avoid duplicates
              const singleDayEvents: CalendarEvent[] = [];

              // Determine which week this cell belongs to
              const weekIndex = Math.floor(index / 7);
              const cellHeight = weekCellHeights[weekIndex];

              return (
                <Grid.Col key={index} span={12 / 7}>
                  <Paper
                    p='xs'
                    h={cellHeight}
                    withBorder
                    style={{
                      backgroundColor: isCurrentMonth ? 'white' : '#f8f9fa',
                      borderColor: isToday ? '#228be6' : '#dee2e6',
                      borderWidth: isToday ? 2 : 1,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: cellHeight
                    }}
                  >
                    {/* Date header - fixed at top */}
                    <Box
                      style={{
                        height: 32,
                        paddingTop: 4,
                        paddingBottom: 4,
                        borderBottom: '1px solid #e9ecef',
                        flexShrink: 0
                      }}
                    >
                      <Group justify='space-between' h='100%'>
                        <Text
                          size='sm'
                          fw={isToday ? 600 : 400}
                          c={isCurrentMonth ? 'dark' : 'dimmed'}
                        >
                          {day.getDate()}
                        </Text>
                        {dayEvents.length > 0 && (
                          <Badge size='xs' variant='filled' color='blue'>
                            {dayEvents.length}
                          </Badge>
                        )}
                      </Group>
                    </Box>

                    {/* Scrollable content area for single-day events only */}
                    <Box
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingTop: 4,
                        paddingBottom: 4
                      }}
                    >
                      <Stack gap={2}>
                        {/* Single day events only */}
                        {singleDayEvents.slice(0, 3).map((event) => (
                          <Box
                            key={event.id}
                            style={{
                              padding: '4px 6px',
                              backgroundColor: event.color,
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 500,
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedEvent(event);
                              setEventModalOpened(true);
                            }}
                          >
                            <Text size='xs' truncate style={{ color: 'white', fontSize: '11px' }}>
                              {event.title}
                            </Text>
                          </Box>
                        ))}

                        {singleDayEvents.length > 3 && (
                          <Text size='xs' c='dimmed' ta='center' style={{ padding: '2px 0' }}>
                            +{singleDayEvents.length - 3} more
                          </Text>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid.Col>
              );
            })}
          </Grid>

          {/* Continuous ribbon overlay spanning across multiple day cells */}
          {ribbonEvents.map((ribbon) => {
            const weekIndex = ribbon.row;
            const cellWidth = 'calc((100% - 6px) / 7)'; // Account for grid gaps
            const leftPosition = `calc(${ribbon.startCol} * (${cellWidth} + 1px))`;
            const ribbonWidth = `calc(${ribbon.endCol - ribbon.startCol + 1} * ${cellWidth} + ${ribbon.endCol - ribbon.startCol}px)`;
            // Calculate top position based on dynamic week cell height and ribbon layer
            const weekStartY = weekCellHeights.slice(0, weekIndex).reduce((a, b) => a + b, 0);
            const topPosition = weekStartY + 36 + ribbon.layer * ribbonLayerHeight;

            const borderRadius = (() => {
              if (ribbon.startCol === ribbon.endCol) {
                return '8px';
              } else {
                return '8px';
              }
            })();

            return (
              <Box
                key={`ribbon-${ribbon.event.id}-week${ribbon.row}-col${ribbon.startCol}-${ribbon.endCol}-layer${ribbon.layer}`}
                style={{
                  position: 'absolute',
                  left: leftPosition,
                  top: topPosition,
                  width: ribbonWidth,
                  height: ribbonLayerHeight - 2,
                  background: `linear-gradient(135deg, ${ribbon.event.color}, ${ribbon.event.color}dd)`,
                  color: 'white',
                  borderRadius,
                  cursor: 'pointer',
                  paddingLeft: 6,
                  paddingRight: 6,
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '11px',
                  fontWeight: 500,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease',
                  zIndex: 100 + ribbon.layer,
                  pointerEvents: 'auto'
                }}
                onClick={() => {
                  setSelectedEvent(ribbon.event);
                  setEventModalOpened(true);
                }}
              >
                <Text size='xs' truncate style={{ color: 'white', fontSize: '11px' }}>
                  {ribbon.event.title}
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderWeekView = () => (
    <Box>
      <Grid gutter='sm'>
        {calendarDays.map((day, index) => {
          const dayEvents = getEventsForDay(day);
          const isToday = day.toDateString() === new Date().toDateString();

          return (
            <Grid.Col key={index} span={12 / 7}>
              <Paper p='md' withBorder style={{ minHeight: 300 }}>
                <Group justify='space-between' mb='md'>
                  <Box>
                    <Text fw={500} size='sm'>
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </Text>
                    <Text size='lg' fw={isToday ? 600 : 400} c={isToday ? 'blue' : 'dark'}>
                      {day.getDate()}
                    </Text>
                  </Box>
                  {dayEvents.length > 0 && (
                    <Badge size='sm' variant='filled' color='blue'>
                      {dayEvents.length}
                    </Badge>
                  )}
                </Group>

                <Stack gap='xs'>{dayEvents.map((event) => renderEventItem(event, day))}</Stack>
              </Paper>
            </Grid.Col>
          );
        })}
      </Grid>
    </Box>
  );

  const renderDayView = () => {
    const dayEvents = getEventsForDay(calendarDays[0]);
    const isToday = calendarDays[0].toDateString() === new Date().toDateString();

    return (
      <Paper p='lg' withBorder>
        <Group justify='space-between' mb='lg'>
          <Box>
            <Text size='xl' fw={600} c={isToday ? 'blue' : 'dark'}>
              {calendarDays[0].toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </Text>
            <Text c='dimmed'>
              {dayEvents.length} course{dayEvents.length !== 1 ? 's' : ''} scheduled
            </Text>
          </Box>
        </Group>

        <Stack gap='md'>
          {dayEvents.length === 0 ? (
            <Box py={60} ta='center'>
              <IconCalendarEvent size={48} color='gray' />
              <Text c='dimmed' mt='md'>
                No courses scheduled for this day
              </Text>
            </Box>
          ) : (
            dayEvents.map((event) => (
              <Paper key={event.id} p='md' withBorder>
                <Group justify='space-between'>
                  <Box flex={1}>
                    <Text fw={500} size='lg'>
                      {event.title}
                    </Text>
                    <Group gap='xs' mt='xs'>
                      <Badge variant='outline' size='sm'>
                        {event.course.courseCode}
                      </Badge>
                      <Badge variant='light' color='green' size='sm'>
                        {event.course.learningHours}h
                      </Badge>
                      <Badge variant='light' color='blue' size='sm'>
                        {event.course.modeOfDelivery || event.course.deliveryType}
                      </Badge>
                    </Group>
                    <Text size='sm' c='dimmed' mt='xs'>
                      {event.start.toLocaleDateString()} - {event.end.toLocaleDateString()}
                    </Text>
                  </Box>
                  <Tooltip label='View details'>
                    <ActionIcon
                      variant='subtle'
                      onClick={() => {
                        setSelectedEvent(event);
                        setEventModalOpened(true);
                      }}
                    >
                      <IconInfoCircle size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Paper>
            ))
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      <Paper p='md' radius='md'>
        {/* Calendar Header */}
        <Group justify='space-between' mb='lg'>
          <Group>
            <ActionIcon variant='subtle' onClick={handlePrevious}>
              <IconArrowLeft size={18} />
            </ActionIcon>
            <Text fw={600} size='lg' style={{ minWidth: 200, textAlign: 'center' }}>
              {formatDateHeader()}
            </Text>
            <ActionIcon variant='subtle' onClick={handleNext}>
              <IconArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
            </ActionIcon>
          </Group>

          <SegmentedControl
            value={calendarView}
            onChange={(value) => setCalendarView(value as CalendarViewType)}
            data={[
              { label: 'Month', value: 'month' },
              { label: 'Week', value: 'week' },
              { label: 'Day', value: 'day' }
            ]}
          />
        </Group>

        {/* Calendar Content */}
        {calendarView === 'month' && renderMonthView()}
        {calendarView === 'week' && renderWeekView()}
        {calendarView === 'day' && renderDayView()}
      </Paper>

      {/* Event Details Modal (Read-only) */}
      {selectedEvent && (
        <Modal
          opened={eventModalOpened}
          onClose={() => {
            setEventModalOpened(false);
            setHistoryExpanded(false);
          }}
          title={null}
          size='lg'
          padding={0}
          radius='xl'
          withCloseButton={false}
          centered
          overlayProps={{
            backgroundOpacity: 0.55,
            blur: 8
          }}
          styles={{
            content: {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: 0
            },
            body: {
              padding: 0
            }
          }}
        >
          <Box
            style={{
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Header with Course Color */}
            <Box
              p='xl'
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}, ${selectedEvent.color}dd)`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Background Pattern */}
              <Box
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  transform: 'translate(50%, -50%)'
                }}
              />

              <Group
                justify='space-between'
                align='flex-start'
                style={{ position: 'relative', zIndex: 1 }}
              >
                <Box>
                  <Title order={2} c='white' fw={700} mb='xs'>
                    {selectedEvent.course.courseName}
                  </Title>

                  <Group gap='md' mb='md'>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      📚 {selectedEvent.course.courseCode}
                    </Badge>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      ⏱️ {selectedEvent.course.learningHours} Hours
                    </Badge>
                    <Badge
                      variant='white'
                      size='lg'
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      📋 {selectedEvent.course.modeOfDelivery || selectedEvent.course.deliveryType}
                    </Badge>
                  </Group>

                  <Text c='rgba(255,255,255,0.9)' size='sm' fw={500}>
                    Course Schedule Details
                  </Text>
                </Box>

                <ActionIcon
                  variant='subtle'
                  color='white'
                  size='lg'
                  radius='xl'
                  onClick={() => {
                    setEventModalOpened(false);
                    setHistoryExpanded(false);
                  }}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                >
                  <IconX size={20} />
                </ActionIcon>
              </Group>
            </Box>

            {/* Content */}
            <Box p='xl'>
              <Stack gap='xl'>
                {/* Schedule Information */}
                <Box>
                  <Group gap='sm' mb='md'>
                    <IconCalendarEvent size={20} color={selectedEvent.color} />
                    <Title order={4} c='dark'>
                      Schedule Information
                    </Title>
                  </Group>

                  <Paper
                    p='md'
                    radius='lg'
                    style={{
                      backgroundColor: '#f8f9ff',
                      border: `1px solid ${selectedEvent.color}30`
                    }}
                  >
                    <Grid>
                      <Grid.Col span={6}>
                        <Box>
                          <Text size='sm' c='dimmed' fw={500}>
                            START DATE
                          </Text>
                          <Text fw={600} c='dark' size='lg'>
                            {selectedEvent.start.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Text>
                        </Box>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Box>
                          <Text size='sm' c='dimmed' fw={500}>
                            END DATE
                          </Text>
                          <Text fw={600} c='dark' size='lg'>
                            {selectedEvent.end.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </Text>
                        </Box>
                      </Grid.Col>
                    </Grid>

                    <Group mt='md' gap='md'>
                      <Group gap='xs'>
                        <IconClock size={16} color={selectedEvent.color} />
                        <Text size='sm' fw={500}>
                          Duration: {selectedEvent.course.learningHours} hours
                        </Text>
                      </Group>
                    </Group>
                  </Paper>
                </Box>

                {/* Module Breakdown Section */}
                {selectedEvent.course.listModule && selectedEvent.course.listModule.length > 0 && (
                  <Box>
                    <Group gap='sm' mb='md'>
                      <IconList size={20} color={selectedEvent.color} />
                      <Title order={4} c='dark'>
                        Module Breakdown
                      </Title>
                    </Group>

                    <Stack gap='sm'>
                      {[...selectedEvent.course.listModule]
                        .sort((a, b) => {
                          // Sort modules by start date (earliest first)
                          if (!a.moduleStart && !b.moduleStart) return 0;
                          if (!a.moduleStart) return 1;
                          if (!b.moduleStart) return -1;
                          return (
                            new Date(ensureUTCFormat(a.moduleStart)).getTime() -
                            new Date(ensureUTCFormat(b.moduleStart)).getTime()
                          );
                        })
                        .map((module, moduleIndex) => (
                          <Paper
                            key={moduleIndex}
                            p='md'
                            radius='lg'
                            style={{
                              backgroundColor: '#f8f9ff',
                              border: `1px solid ${selectedEvent.color}30`
                            }}
                          >
                            <Group justify='space-between' align='flex-start' mb='sm'>
                              <Badge variant='filled' color={selectedEvent.color} size='lg'>
                                {module.moduleType}
                              </Badge>
                            </Group>

                            <Grid>
                              <Grid.Col span={6}>
                                <Text size='sm' c='dimmed' fw={500}>
                                  MODULE START
                                </Text>
                                <Text fw={600} c='dark'>
                                  {new Date(ensureUTCFormat(module.moduleStart)).toLocaleDateString(
                                    'en-US',
                                    {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    }
                                  )}
                                </Text>
                                <Text size='sm' c='blue' fw={500}>
                                  {new Date(ensureUTCFormat(module.moduleStart)).toLocaleTimeString(
                                    'en-US',
                                    {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false
                                    }
                                  )}
                                </Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Text size='sm' c='dimmed' fw={500}>
                                  MODULE END
                                </Text>
                                <Text fw={600} c='dark'>
                                  {new Date(ensureUTCFormat(module.moduleEnd)).toLocaleDateString(
                                    'en-US',
                                    {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    }
                                  )}
                                </Text>
                                <Text size='sm' c='blue' fw={500}>
                                  {new Date(ensureUTCFormat(module.moduleEnd)).toLocaleTimeString(
                                    'en-US',
                                    {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false
                                    }
                                  )}
                                </Text>
                              </Grid.Col>
                            </Grid>
                          </Paper>
                        ))}
                    </Stack>
                  </Box>
                )}

                {/* Budget and CEA Ready Status */}
                <Box>
                  <Group gap='sm' mb='md'>
                    <IconInfoCircle size={20} color={selectedEvent.color} />
                    <Title order={4} c='dark'>
                      Course Details
                    </Title>
                  </Group>

                  <Grid>
                    <Grid.Col span={6}>
                      <Paper
                        p='md'
                        radius='lg'
                        style={{
                          backgroundColor: '#f8f9ff',
                          border: `1px solid ${selectedEvent.color}30`
                        }}
                      >
                        <Text size='sm' c='dimmed' fw={500} mb='xs'>
                          BUDGET PLANNING
                        </Text>
                        <Text fw={600} c='dark' size='lg'>
                          {selectedEvent.course.budget !== undefined &&
                          selectedEvent.course.budget !== null
                            ? new Intl.NumberFormat('id-ID', {
                                style: 'currency',
                                currency: 'IDR',
                                maximumFractionDigits: 0
                              }).format(selectedEvent.course.budget)
                            : 'Rp 0 (No budget allocated)'}
                        </Text>
                      </Paper>
                    </Grid.Col>

                    {selectedEvent.course.isCEAReady !== undefined && (
                      <Grid.Col span={6}>
                        <Paper
                          p='md'
                          radius='lg'
                          style={{
                            backgroundColor: '#f8f9ff',
                            border: `1px solid ${selectedEvent.color}30`
                          }}
                        >
                          <Text size='sm' c='dimmed' fw={500} mb='xs'>
                            CEA READY STATUS
                          </Text>
                          <Badge
                            variant='filled'
                            color={selectedEvent.course.isCEAReady ? 'green' : 'red'}
                            size='lg'
                          >
                            {selectedEvent.course.isCEAReady ? 'Ready' : 'Not Ready'}
                          </Badge>
                        </Paper>
                      </Grid.Col>
                    )}

                    {selectedEvent.course.statusCourse && (
                      <Grid.Col span={12}>
                        <Paper
                          p='md'
                          radius='lg'
                          style={{
                            backgroundColor: '#f8f9ff',
                            border: `1px solid ${selectedEvent.color}30`
                          }}
                        >
                          <Text size='sm' c='dimmed' fw={500} mb='xs'>
                            COURSE STATUS
                          </Text>
                          <Badge
                            variant='filled'
                            color={
                              selectedEvent.course.statusCourse === 'planned'
                                ? 'blue'
                                : selectedEvent.course.statusCourse === 'canceled'
                                  ? 'red'
                                  : 'orange'
                            }
                            size='lg'
                          >
                            {selectedEvent.course.statusCourse.toUpperCase()}
                          </Badge>
                        </Paper>
                      </Grid.Col>
                    )}
                  </Grid>
                </Box>

                {/* History Transactions - Collapsible */}
                {selectedEvent.course.historyTransaksi &&
                  selectedEvent.course.historyTransaksi.length > 0 && (
                    <Box>
                      <Group
                        gap='sm'
                        mb='md'
                        style={{ cursor: 'pointer' }}
                        onClick={() => setHistoryExpanded(!historyExpanded)}
                      >
                        <IconHistory size={20} color={selectedEvent.color} />
                        <Title order={4} c='dark'>
                          Schedule History
                        </Title>
                        <Badge variant='filled' size='sm' color={selectedEvent.color}>
                          {selectedEvent.course.historyTransaksi.length} records
                        </Badge>
                        <ActionIcon
                          variant='subtle'
                          size='sm'
                          style={{
                            transform: historyExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}
                        >
                          <IconChevronDown size={16} />
                        </ActionIcon>
                      </Group>

                      {historyExpanded && (
                        <Stack gap='xs'>
                          {selectedEvent.course.historyTransaksi.map((history, index) => {
                            const startDate = new Date(ensureUTCFormat(history.startDate));
                            const endDate = new Date(ensureUTCFormat(history.endDate));
                            return (
                              <Paper
                                key={index}
                                p='sm'
                                radius='md'
                                style={{
                                  backgroundColor: '#f8f9ff',
                                  border: `1px solid ${selectedEvent.color}20`
                                }}
                              >
                                <Group justify='space-between'>
                                  <Box>
                                    <Text size='xs' c='dimmed' fw={500}>
                                      FROM
                                    </Text>
                                    <Text size='sm' fw={500} c='dark'>
                                      {startDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                    <Text size='xs' c='blue'>
                                      {startDate.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      })}
                                    </Text>
                                  </Box>
                                  <Box ta='center'>
                                    <IconArrowRight size={16} color='gray' />
                                  </Box>
                                  <Box>
                                    <Text size='xs' c='dimmed' fw={500}>
                                      TO
                                    </Text>
                                    <Text size='sm' fw={500} c='dark'>
                                      {endDate.toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </Text>
                                    <Text size='xs' c='blue'>
                                      {endDate.toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      })}
                                    </Text>
                                  </Box>
                                </Group>
                              </Paper>
                            );
                          })}
                        </Stack>
                      )}

                      {!historyExpanded && (
                        <Text ta='center' size='sm' c='dimmed' style={{ fontStyle: 'italic' }}>
                          Click to view {selectedEvent.course.historyTransaksi.length} schedule
                          history records
                        </Text>
                      )}
                    </Box>
                  )}
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              p='xl'
              style={{
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              <Group justify='flex-end'>
                <Button
                  onClick={() => {
                    setEventModalOpened(false);
                    setHistoryExpanded(false);
                  }}
                  size='md'
                  radius='lg'
                  style={{
                    backgroundColor: selectedEvent.color,
                    '&:hover': {
                      backgroundColor: selectedEvent.color,
                      opacity: 0.9
                    }
                  }}
                >
                  Close
                </Button>
              </Group>
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default ReadOnlyCalendarScheduleView;
